import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Eye,
  Users,
  Calendar,
  TrendingUp,
  Smartphone,
  Monitor,
  Tablet,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("en-NZ").format(n || 0);
const dayLabel = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-[#D5D1C8] rounded-md p-5 shadow-sm">
      <div className="flex items-center gap-2 text-[#4A4F4A]">
        <Icon className="w-4 h-4" strokeWidth={2} />
        <span className="text-xs font-bold uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p className="mt-3 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A]">
        {fmt(value)}
      </p>
      {sub != null && (
        <p className="mt-1 text-xs text-[#4A4F4A]">{sub}</p>
      )}
    </div>
  );
}

function DeviceIcon({ name }) {
  if (name === "mobile") return <Smartphone className="w-4 h-4" />;
  if (name === "tablet") return <Tablet className="w-4 h-4" />;
  if (name === "desktop") return <Monitor className="w-4 h-4" />;
  return <Eye className="w-4 h-4" />;
}

export default function Stats() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("Missing ?token=… in the URL.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/admin/stats`, { params: { token } })
      .then((res) => setData(res.data))
      .catch((err) => {
        setError(
          err?.response?.status === 401
            ? "Invalid token."
            : "Could not load stats.",
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <p className="text-[#4A4F4A]">Loading…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6">
        <div className="max-w-md bg-white border border-[#D5D1C8] rounded-md p-8 shadow-sm text-center">
          <Lock className="w-8 h-8 text-[#D96C32] mx-auto" />
          <h1 className="mt-4 font-display font-bold text-2xl text-[#1A1D1A]">
            Restricted
          </h1>
          <p className="mt-3 text-sm text-[#4A4F4A]">{error}</p>
          <p className="mt-4 text-xs text-[#4A4F4A]/80">
            Add your token to the URL: <code>/stats?token=…</code>
          </p>
        </div>
      </main>
    );
  }

  const { totals, last_7_days, device_breakdown, top_referrers } = data;

  // Build mini bar chart for last 7 days
  const max = Math.max(1, ...last_7_days.map((d) => d.visits));

  const totalDevice = device_breakdown.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <main className="min-h-screen bg-[#F9F8F6] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#D96C32]">
              Internal · Arvo Stats
            </span>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A]">
              Visitor counter
            </h1>
            <p className="mt-1 text-sm text-[#4A4F4A]">
              Privacy-friendly. No IPs or cookies stored.
            </p>
          </div>
          <a
            href="/"
            className="text-sm font-semibold text-[#2D4A22] hover:underline"
          >
            ← Back to site
          </a>
        </div>

        {/* Top stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Eye}
            label="All-time visits"
            value={totals.all_time_visits}
            sub={`${fmt(totals.all_time_sessions)} unique sessions`}
          />
          <StatCard
            icon={Calendar}
            label="Today"
            value={totals.today_visits}
            sub={`${fmt(totals.today_sessions)} unique today`}
          />
          <StatCard
            icon={TrendingUp}
            label="Last 30 days"
            value={totals.last_30d_visits}
            sub={`${fmt(totals.last_30d_sessions)} unique`}
          />
          <StatCard
            icon={Users}
            label="Avg / session"
            value={
              totals.all_time_sessions
                ? Math.round(
                    (totals.all_time_visits / totals.all_time_sessions) * 10,
                  ) / 10
                : 0
            }
            sub="page loads per session"
          />
        </div>

        {/* 7-day chart */}
        <div className="mt-8 bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm">
          <h2 className="font-display font-bold text-lg text-[#1A1D1A]">
            Last 7 days
          </h2>
          <div className="mt-6 grid grid-cols-7 gap-2 sm:gap-4 items-end h-44">
            {last_7_days.map((d) => {
              const h = (d.visits / max) * 100;
              return (
                <div
                  key={d.date}
                  className="flex flex-col items-center justify-end h-full"
                  title={`${d.visits} visits · ${d.sessions} sessions`}
                >
                  <span className="text-xs font-bold text-[#1A1D1A]">
                    {d.visits}
                  </span>
                  <div
                    className="mt-1 w-full rounded-t bg-[#2D4A22]"
                    style={{ height: `${Math.max(h, 4)}%` }}
                  />
                  <span className="mt-2 text-[11px] text-[#4A4F4A]">
                    {dayLabel(d.date)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom row: device + referrers */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm">
            <h2 className="font-display font-bold text-lg text-[#1A1D1A]">
              Device split
            </h2>
            <ul className="mt-5 space-y-3">
              {device_breakdown.length === 0 && (
                <li className="text-sm text-[#4A4F4A]">No data yet.</li>
              )}
              {device_breakdown.map((d) => {
                const pct = Math.round((d.count / totalDevice) * 100);
                return (
                  <li key={d.device}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-[#1A1D1A] capitalize">
                        <DeviceIcon name={d.device} /> {d.device}
                      </span>
                      <span className="font-bold text-[#1A1D1A]">
                        {fmt(d.count)}{" "}
                        <span className="text-[#4A4F4A] font-normal">
                          ({pct}%)
                        </span>
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 bg-[#EAE7DF] rounded">
                      <div
                        className="h-2 rounded bg-[#2D4A22]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm">
            <h2 className="font-display font-bold text-lg text-[#1A1D1A]">
              Top referrers
            </h2>
            <ul className="mt-5 divide-y divide-[#EAE7DF]">
              {top_referrers.length === 0 && (
                <li className="text-sm text-[#4A4F4A]">No data yet.</li>
              )}
              {top_referrers.map((r) => (
                <li
                  key={r.source}
                  className="py-2.5 flex items-center justify-between text-sm"
                >
                  <span className="text-[#1A1D1A] truncate">{r.source}</span>
                  <span className="font-bold text-[#1A1D1A]">
                    {fmt(r.count)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </main>
  );
}
